/**
 * Client brand selection.
 *
 * The brand is chosen at build time with `VITE_BRAND` (e.g.
 * `VITE_BRAND=nomura npm run build`); unset or unknown values fall back to
 * Lloyds Banking Group. Colours live in styles.css under
 * `[data-brand="…"]`, which main.tsx applies to <html>.
 */

export type BrandId = 'lbg' | 'nomura';

export interface Brand {
	id: BrandId;
	/** Display name: app bar heading, password gate, card watermarks, page title. */
	name: string;
	/** Optional wordmark shown in place of the text name (name stays as the accessible label). */
	logo?: { viewBox: string; fill: string; paths: string[] };
}

const BRANDS: Record<BrandId, Brand> = {
	lbg: { id: 'lbg', name: 'Lloyds Banking Group' },
	nomura: {
		id: 'nomura',
		name: 'Nomura',
		// Nomura corporate wordmark (Wikimedia Commons "Nomura logo.svg").
		logo: {
			viewBox: '0 0 498 86',
			fill: '#C90813',
			paths: [
				'M77 43.004C77 14.348 94.913 0 117.004 0 139.096 0 157 14.348 157 43.004 157 71.708 139.096 86 117.004 86 94.913 86 77 71.843 77 43.004M117.004 75.1c10.899 0 19.734-11.373 19.734-32.103 0-20.532-8.835-32.099-19.734-32.099-10.902 0-19.742 11.567-19.742 32.099-.004 20.592 8.835 32.103 19.742 32.103',
				'M0 3v79h13.797V28.933L56.4 82H68V3H54.866v43.548L19.49 3zm446.367 0L414 82h13.98l8.923-21.72h29.388L475.322 82H498L465.304 3h-18.937zm-4.953 45.93l10.127-24.75 10.135 24.75h-20.262zm-125.396 6.34c-.368 7.315-.936 14.551-9.04 17.256-3.606 1.195-10.254 1.169-13.994-.329-1.79-.727-2.908-1.346-4.303-2.73-2.696-2.662-3.762-8.32-3.862-12.007V3H264v52.27c.2 7.228 1.36 14.486 6.054 20.303 8.07 9.985 22.865 11.41 34.826 9.933 7.433-.922 13.955-3.384 19.177-8.846 3.61-3.77 5.86-9.717 6.505-14.837.377-2.973.438-3.462.438-6.553V3h-14.986v52.27h.004zm49.562-41.131c9.11 0 18.16-1.543 22.125 7.89 1.563 3.724 1.666 12.18-1.976 15.776-4.34 4.277-10.996 4.693-16.744 5.181L390.223 82H412l-19.266-34.792c11.486-4.007 16.88-10.47 16.313-22.818C408.43 11.057 397.125 3 384.196 3H345v79h20.58V14.139zM231 82L200.157 3H183.96L154 82h14.538l20.165-53.388L209.19 82zm1.145-79h-16.967L210 16.509l7.379 18.909 2.647-6.806L239.966 82H263z',
				'M194.318 55L184 83h13.17L201 72.455z',
			],
		},
	},
};

export function resolveBrand(id: string | undefined): Brand {
	return BRANDS[id as BrandId] ?? BRANDS.lbg;
}

export const BRAND: Brand = resolveBrand(import.meta.env.VITE_BRAND);
